import { useEffect, useRef, useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { Icon } from "./Icon.tsx";

const UPDATE_INTERVAL_MS = 60 * 60 * 1000;

export function SWUpdatePrompt() {
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const [entered, setEntered] = useState(false);
  const [updating, setUpdating] = useState(false);
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_workerUrl, registration) {
      registrationRef.current = registration ?? null;
      void registration?.update().catch(() => undefined);
    },
  });

  useEffect(() => {
    const requestUpdate = () => {
      if (document.visibilityState === "visible") {
        void registrationRef.current?.update().catch(() => undefined);
      }
    };
    const timer = window.setInterval(requestUpdate, UPDATE_INTERVAL_MS);
    window.addEventListener("focus", requestUpdate);
    document.addEventListener("visibilitychange", requestUpdate);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", requestUpdate);
      document.removeEventListener("visibilitychange", requestUpdate);
    };
  }, []);

  useEffect(() => {
    if (!needRefresh) {
      setEntered(false);
      setUpdating(false);
      return;
    }
    const frame = window.requestAnimationFrame(() => setEntered(true));
    return () => window.cancelAnimationFrame(frame);
  }, [needRefresh]);

  if (!needRefresh) return null;

  const applyUpdate = async () => {
    setUpdating(true);
    const currentController = navigator.serviceWorker.controller;
    let reloading = false;
    let fallbackTimer = 0;
    const reload = () => {
      if (reloading) return;
      reloading = true;
      window.clearTimeout(fallbackTimer);
      window.location.reload();
    };
    const onControllerChange = () => reload();
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange, { once: true });
    try {
      await updateServiceWorker(false);
      fallbackTimer = window.setTimeout(() => {
        if (navigator.serviceWorker.controller !== currentController) reload();
        else {
          navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
          setUpdating(false);
        }
      }, 4000);
    } catch {
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
      setUpdating(false);
    }
  };

  return (
    <aside
      className={`sw-update-prompt ${entered ? "sw-update-entered" : ""}`}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-label="Доступно обновление приложения"
    >
      <span className="sw-update-icon"><Icon name="rotate" /></span>
      <span className="sw-update-copy">
        <strong>Доступна новая версия</strong>
        <small>Обновите приложение, чтобы получить последние изменения.</small>
      </span>
      <button
        className="button button-primary sw-update-action"
        type="button"
        disabled={updating}
        onClick={() => void applyUpdate()}
      >
        {updating ? "Обновляем…" : "Обновить"}
      </button>
      <button
        className="icon-button sw-update-close"
        type="button"
        onClick={() => setNeedRefresh(false)}
        aria-label="Закрыть предложение обновления"
      >
        <Icon name="x" />
      </button>
    </aside>
  );
}
